import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Award, Calendar, CheckCircle2, AlertTriangle, XCircle,
} from "lucide-react";

// Rich Employee Skills tab.
// Read/write via /employees/{eid}/skill-entries. Strictly additive, coexists
// with the existing chip-style `skill_ids[]` selector on the Details tab.

const PRIORITY_TONE = {
  essential: "red",
  secondary: "blue",
  training:  "amber",
};
const PROFICIENCY_TONE = {
  beginner:     "slate",
  intermediate: "blue",
  advanced:     "green",
  expert:       "purple",
};
const PRIORITY_LABEL = { essential: "Essential", secondary: "Secondary", training: "Training" };
const PROFICIENCY_LABEL = {
  beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced", expert: "Expert",
};

const EXPIRY_WARN_DAYS = 30;

function certStatus(expiry) {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { tone: "red", label: "Expired", icon: XCircle };
  if (diff <= EXPIRY_WARN_DAYS) return { tone: "amber", label: `Expires in ${diff}d`, icon: AlertTriangle };
  return { tone: "green", label: "Up to date", icon: CheckCircle2 };
}

const EMPTY_ENTRY = {
  skill_id: "",
  priority: "secondary",
  proficiency: "intermediate",
  cert_issue_date: "",
  cert_expiry_date: "",
  cert_reference: "",
  notes: "",
};

export default function EmployeeSkillsTab({ employeeId }) {
  const [skillsLibrary, setSkillsLibrary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: sk }, { data: en }] = await Promise.all([
        api.get("/skills"),
        api.get(`/employees/${employeeId}/skill-entries`),
      ]);
      setSkillsLibrary(sk.filter((s) => !s.is_archived));
      setEntries(en);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load skills");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);

  const usedSkillIds = useMemo(() => new Set(entries.map((e) => e.skill_id)), [entries]);
  const availableSkills = useMemo(
    () => skillsLibrary.filter((s) =>
      !usedSkillIds.has(s.skill_id) || (editing && editing.skill_id === s.skill_id)),
    [skillsLibrary, usedSkillIds, editing]
  );

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_ENTRY);
    setOpen(true);
  };
  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      skill_id: entry.skill_id,
      priority: entry.priority || "secondary",
      proficiency: entry.proficiency || "intermediate",
      cert_issue_date: entry.cert_issue_date || "",
      cert_expiry_date: entry.cert_expiry_date || "",
      cert_reference: entry.cert_reference || "",
      notes: entry.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.skill_id) {
      toast.error("Choose a skill");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        skill_id: form.skill_id,
        priority: form.priority,
        proficiency: form.proficiency,
        cert_issue_date: form.cert_issue_date || null,
        cert_expiry_date: form.cert_expiry_date || null,
        cert_reference: form.cert_reference || null,
        notes: form.notes || null,
      };
      await api.post(`/employees/${employeeId}/skill-entries`, payload);
      toast.success(editing ? "Skill updated" : "Skill added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove ${entry.skill_name || "this skill"} from this employee?`)) return;
    try {
      await api.delete(`/employees/${employeeId}/skill-entries/${entry.skill_id}`);
      toast.success("Skill removed");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="employee-skills-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow">Skills, Importance & Certifications</div>
          <p className="text-xs text-[#4B5773] mt-1 max-w-xl">
            Set each employee&apos;s skill level and importance, and record any certifications
            with expiry dates. Recording certifications lets NextRota warn you when one is about
            to expire, before a scheduled shift fails compliance.
          </p>
        </div>
        <Button type="button" onClick={openAdd} data-testid="emp-skill-add-btn"
          className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] h-9">
          <Plus className="h-4 w-4 mr-1" />Add skill
        </Button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="emp-skills-table">
          <thead className="bg-[#F6F9FE]">
            <tr className="text-left text-[#4B5773]">
              <th className="py-2.5 px-2.5 eyebrow font-normal">Skill</th>
              <th className="py-2.5 px-2.5 eyebrow font-normal">Skill Type</th>
              <th className="py-2.5 px-2.5 eyebrow font-normal">Importance</th>
              <th className="py-2.5 px-2.5 eyebrow font-normal">Skill Level</th>
              <th className="py-2.5 px-2.5 eyebrow font-normal">Certification</th>
              <th className="py-2.5 px-2.5 eyebrow font-normal text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading && (
              <tr><td colSpan={6} className="py-8 text-center text-[#4B5773]">Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-[#4B5773]"
                      data-testid="emp-skills-empty">
                No detailed skill entries yet. Click &ldquo;Add skill&rdquo; to track importance, skill level and certifications.
              </td></tr>
            )}
            {entries.map((en) => {
              const cs = certStatus(en.cert_expiry_date);
              const CIcon = cs?.icon;
              return (
                <tr key={en.skill_id} data-testid={`emp-skill-row-${en.skill_id}`}>
                  <td className="py-2.5 px-2.5">
                    <div className="font-medium text-[#0B1A36]">{en.skill_name || "-"}</div>
                    {en.notes && (
                      <div className="text-[11px] text-[#4B5773] truncate max-w-xs">{en.notes}</div>
                    )}
                  </td>
                  <td className="py-2.5 px-2.5 whitespace-nowrap">
                    <Badge tone={en.skill_kind === "assignment" ? "blue" : "slate"}>
                      {en.skill_kind === "assignment" ? "Assignment" : "Additional"}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-2.5 whitespace-nowrap">
                    <Badge tone={PRIORITY_TONE[en.priority] || "slate"}
                      testid={`emp-skill-priority-${en.skill_id}`}>
                      {PRIORITY_LABEL[en.priority] || en.priority}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-2.5 whitespace-nowrap">
                    <Badge tone={PROFICIENCY_TONE[en.proficiency] || "slate"}
                      testid={`emp-skill-prof-${en.skill_id}`}>
                      <Award className="h-3 w-3 mr-1" />
                      {PROFICIENCY_LABEL[en.proficiency] || en.proficiency}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-2.5 whitespace-nowrap">
                    {cs ? (
                      <div className="space-y-0.5">
                        <Badge tone={cs.tone} testid={`emp-skill-cert-${en.skill_id}`}>
                          {CIcon && <CIcon className="h-3 w-3 mr-1" />}{cs.label}
                        </Badge>
                        <div className="text-[11px] text-[#4B5773] flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {en.cert_expiry_date}
                          {en.cert_reference && <span className="ml-1">· {en.cert_reference}</span>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-[11px] text-[#4B5773]">-</span>
                    )}
                  </td>
                  <td className="py-2.5 px-2.5 text-right space-x-1 whitespace-nowrap">
                    <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(en)}
                      data-testid={`emp-skill-edit-${en.skill_id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(en)}
                      data-testid={`emp-skill-remove-${en.skill_id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emp-skill-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit skill entry" : "Add skill"}</DialogTitle>
            <DialogDescription>
              Set the importance and skill level for this skill, plus any certification details.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="eyebrow">Skill</Label>
              <Select value={form.skill_id} disabled={!!editing}
                onValueChange={(v) => setForm({ ...form, skill_id: v })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="emp-skill-select">
                  <SelectValue placeholder="Choose a skill from the library" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {availableSkills.length === 0 && (
                    <div className="px-3 py-2 text-xs text-[#4B5773]">
                      No skills available. Add some on the Skills page first.
                    </div>
                  )}
                  {availableSkills.map((s) => (
                    <SelectItem key={s.skill_id} value={s.skill_id}>
                      {s.name}
                      <span className="text-[10px] text-[#4B5773] ml-2">
                        {(s.kind || "additional") === "assignment" ? "Assignment" : "Additional"}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="eyebrow">Importance</Label>
                <Select value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="emp-skill-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essential">Essential</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="training">In training</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Skill Level</Label>
                <Select value={form.proficiency}
                  onValueChange={(v) => setForm({ ...form, proficiency: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="emp-skill-prof-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="expert">Expert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-[#F6F9FE] border border-slate-200 space-y-3">
              <div className="eyebrow">Certification (optional)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="eyebrow">Issued</Label>
                  <Input type="date" value={form.cert_issue_date}
                    onChange={(e) => setForm({ ...form, cert_issue_date: e.target.value })}
                    data-testid="emp-skill-issue-date"
                    className="h-10 rounded-lg mt-2" />
                </div>
                <div>
                  <Label className="eyebrow">Certification expiry date</Label>
                  <Input type="date" value={form.cert_expiry_date}
                    onChange={(e) => setForm({ ...form, cert_expiry_date: e.target.value })}
                    data-testid="emp-skill-expiry-date"
                    className="h-10 rounded-lg mt-2" />
                </div>
              </div>
              <div>
                <Label className="eyebrow">Certificate reference (optional)</Label>
                <Input value={form.cert_reference}
                  onChange={(e) => setForm({ ...form, cert_reference: e.target.value })}
                  placeholder="e.g. CIPD-12345"
                  data-testid="emp-skill-reference"
                  className="h-10 rounded-lg mt-2" />
                <p className="text-[11px] text-[#4B5773] mt-1">
                  Internal reference number or document ID for your records.
                </p>
              </div>
            </div>

            <div>
              <Label className="eyebrow">Notes (optional)</Label>
              <Input value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="emp-skill-notes"
                className="h-10 rounded-lg mt-2" />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="emp-skill-submit"
                className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                {busy ? "Saving…" : editing ? "Save changes" : "Add skill"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Badge({ tone = "slate", children, testid }) {
  const TONES = {
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    red:    "bg-red-50 text-red-700 border-red-200",
    blue:   "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    purple: "bg-violet-50 text-violet-700 border-violet-200",
    slate:  "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}
